import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Sparkles, AlertTriangle, Clock, Coffee, Users } from "lucide-react";

// Flash Report Banner — Admin spec 2026-02-07.
//   • Every user sees THEIR OWN stats: late logins this week, break-exceeds,
//     extra breaks.
//   • Users with admin / admin_reports / admin_shifts / admin_leaves also
//     see a team-wide roll-up (delivered as `payload.team` by the backend).
//   • Dismissible per-day via localStorage — resets automatically at
//     midnight local time so the next login sees a fresh banner.
const DISMISS_KEY = "flashReport.dismissed";

function todayLocalISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function FlashReportBanner() {
  const [payload, setPayload] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Skip if already dismissed today.
    try {
      if (localStorage.getItem(DISMISS_KEY) === todayLocalISO()) {
        setDismissed(true);
        return;
      }
    } catch (e) { void e; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shifts/report/flash");
        if (!cancelled) setPayload(data);
      } catch (e) {
        console.debug("[FlashReportBanner] fetch skipped:", e?.response?.status || e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, todayLocalISO()); } catch (e) { void e; }
    setDismissed(true);
  };

  if (dismissed || !payload) return null;
  const mine = payload.mine || {};
  const y = mine.yesterday || {};
  const w = mine.this_week || {};
  const team = payload.team;

  // Skip rendering entirely if the user has nothing to see (no incidents
  // yesterday AND no team payload) — respects the user's time.
  const hasPersonal = y.late_login || y.break_exceeded || (y.extra_breaks > 0)
                      || w.late_logins > 0 || w.break_exceeds > 0 || w.extra_breaks > 0;
  if (!hasPersonal && !team) return null;

  return (
    <div
      data-testid="flash-report-banner"
      className="mx-4 mt-4 mb-2 border border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background rounded-md p-4 relative"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss report for today"
        data-testid="flash-report-dismiss"
        className="absolute top-2 right-2 p-1 rounded-sm hover:bg-secondary/60 text-muted-foreground"
      >
        <X size={14} />
      </button>

      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-primary" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-primary">Daily Flash Report</div>
      </div>

      {/* Personal stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FlashStat
          icon={Clock}
          label="Yesterday"
          value={buildYesterdayText(y)}
          tone={y.late_login || y.break_exceeded || y.extra_breaks ? "amber" : "emerald"}
          testid="flash-yesterday"
        />
        <FlashStat
          icon={AlertTriangle}
          label="Late logins (this week)"
          value={`${w.late_logins || 0} day${(w.late_logins || 0) === 1 ? "" : "s"}`}
          tone={(w.late_logins || 0) > 0 ? "amber" : "emerald"}
          testid="flash-late-logins"
        />
        <FlashStat
          icon={Coffee}
          label="Extra breaks (this week)"
          value={`${w.extra_breaks || 0} · ${w.break_exceeds || 0} over-break${(w.break_exceeds || 0) === 1 ? "" : "s"}`}
          tone={(w.extra_breaks || 0) > 0 || (w.break_exceeds || 0) > 0 ? "amber" : "emerald"}
          testid="flash-breaks"
        />
      </div>

      {/* Team roll-up (TL / HR / Admin only) */}
      {team && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="flash-report-team">
          <div className="flex items-center gap-2 mb-2">
            <Users size={12} className="text-muted-foreground" />
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Team (yesterday)
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FlashStat
              icon={AlertTriangle}
              label="Late logins"
              value={`${team.yesterday_users_late} user${team.yesterday_users_late === 1 ? "" : "s"}`}
              tone={team.yesterday_users_late > 0 ? "amber" : "emerald"}
              testid="flash-team-late"
            />
            <FlashStat
              icon={Coffee}
              label="Break exceeded"
              value={`${team.yesterday_users_break_exceeded} user${team.yesterday_users_break_exceeded === 1 ? "" : "s"}`}
              tone={team.yesterday_users_break_exceeded > 0 ? "amber" : "emerald"}
              testid="flash-team-exceed"
            />
            <FlashStat
              icon={Coffee}
              label="Extra breaks (total)"
              value={`${team.yesterday_extra_breaks_total} incident${team.yesterday_extra_breaks_total === 1 ? "" : "s"}`}
              tone={team.yesterday_extra_breaks_total > 0 ? "amber" : "emerald"}
              testid="flash-team-extra"
            />
          </div>
          {team.top_offenders && team.top_offenders.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground" data-testid="flash-top-offenders">
              <span className="font-semibold text-foreground">Top this week:</span>{" "}
              {team.top_offenders.map((o, i) => (
                <span key={o.email + i}>
                  {i > 0 ? " · " : ""}
                  <span className="font-medium text-foreground">{o.name || o.email}</span>
                  <span className="text-amber-700"> ({o.late_days}L · {o.extra_breaks}X)</span>
                </span>
              ))}
              <span className="ml-2 text-[10px] opacity-70">(L = late logins, X = extra breaks)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function buildYesterdayText(y) {
  const bits = [];
  if (y.late_login) bits.push("Late login");
  if (y.break_exceeded) bits.push("Break exceeded");
  if (y.extra_breaks) bits.push(`${y.extra_breaks} extra break${y.extra_breaks === 1 ? "" : "s"}`);
  return bits.length ? bits.join(" · ") : "All clean ✓";
}

function FlashStat({ icon: Icon, label, value, tone, testid }) {
  const toneClasses = tone === "amber"
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return (
    <div className={`border rounded-sm p-3 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="opacity-70" />
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</span>
      </div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
